import React from "react";

import { useTheme } from '@mui/material/styles';

import { FormattedMessage } from "react-intl";

import { getAccompanimentHistoryById } from "../../services/accompaniment.service";

import { PapiroConsole } from "src/utils/papiroConsole";

import parse from "html-react-parser";

import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import DialogActions from "@mui/material/DialogActions";
import ClearIcon from '@mui/icons-material/Clear';
import CircularProgress from "@mui/material/CircularProgress";
import Button from "@mui/material/Button";
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';

const defaultState = {
  accompanimentList: [],
  loading: false,
  expandedItems: {}
};

const textLengthLimit = 300;

export default function AccompanimentHistoryDialog(props) {
  const {
    open,
    accompanimentSelected,
    handleAccompanimentHistoryDialogOpen,
    intl,
    setMessage
  } = props;

  const [state, setState] = React.useState(defaultState);

  const theme = useTheme();
  const paperRefs = React.useRef({});

  function toggleExpand(itemKey, index, isExpanded) {
    setState(prev => ({
      ...prev,
      expandedItems: { ...(prev.expandedItems || {}), [`${itemKey}-${index}`]: !isExpanded }
    }));

    if (isExpanded) {
      setTimeout(() => {
        const paperEl = paperRefs.current && paperRefs.current[index];
        if (paperEl && typeof paperEl.scrollIntoView === 'function') {
          paperEl.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'nearest' });
        }
      }, 80);
    }
  }

  async function handleGetAccompanimentHistoryById() {
    setState(prevState => ({ ...prevState, loading: true }));

    const accompanimentId = accompanimentSelected ? accompanimentSelected.id : null;

    const response = await getAccompanimentHistoryById(accompanimentId);
  
    if (response.success) {
      const accompanimentList = response.data ? response.data : [];

      setState(prevState => ({
        ...prevState,
        loading: false,
        accompanimentList,
        expandedItems: {}
      }));

    } else {
      setState(prevState => ({ ...prevState, loading: false }));

      setMessage(
        true,
        'error',
        response.data.errors && response.data.errors[0]
          ? response.data.errors[0]
          : intl.formatMessage({ id: "process.error" })
      );
    }
  }

  function stripHtml(html) {
    if (!html) return '';
    return String(html).replace(/<[^>]+>/g, '');
  }

  function truncateText(text, length = textLengthLimit) {
    if (!text) return '';
    if (text.length <= length) return text;
    return text.substring(0, length).trim();
  }

  function renderDescription(isExpanded, description, index, key) {
    const conditionToSpread = stripHtml(description).length > textLengthLimit;

    return (
      <>
        {isExpanded ? (
          <Typography variant="body2" color="text.primary" component="div">
            {parse(description)}
          </Typography>
        ) : (
          <Typography variant="body2" color="text.primary" component="div">
            {conditionToSpread ? truncateText(stripHtml(description), textLengthLimit) : parse(description)}
            {conditionToSpread ? '...' : ''}
          </Typography>
        )}

        {stripHtml(description).length > textLengthLimit && (
          <Typography
            component="span"
            variant="caption"
            sx={{ color: theme.palette.primary.main, cursor: 'pointer', ml: 1 }}
            onClick={() => toggleExpand(key, index, isExpanded)}
          >
            {isExpanded ? `(${intl.formatMessage({ id: "see.less" })})` : `(${intl.formatMessage({ id: "see.more" })})`}
          </Typography>
        )}
      </>
    );
  }

  function renderHistory(item, index) {
    const createdUserName = item.created_user_name ? item.created_user_name : '';
    const createdData = item.created_at_date ? item.created_at_date : '';
    const previusDescription = item.previous_description ? item.previous_description : '';
    const nextDescription = item.next_description ? item.next_description : '';

    const prevKey = "prev";
    const nextKey = "next";

    const isPrevExpanded = !!(state.expandedItems && state.expandedItems[`${prevKey}-${index}`]);
    const isNextExpanded = !!(state.expandedItems && state.expandedItems[`${nextKey}-${index}`]);

    return (
      <Grid item xs={12} key={index}>
        <Paper ref={el => (paperRefs.current[index] = el)} elevation={1} sx={{ p: 2, mb: 2, borderLeft: `4px solid ${theme.palette.primary.main}` }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
            <Box>
              <Typography variant="subtitle2" component="div">
                ✏️ {intl.formatMessage({ id: "accompaniment.history.dialog.change.by" })}: <strong>{createdUserName}</strong>
              </Typography>

              <Typography variant="caption" color="text.secondary">
                {intl.formatMessage({ id: "accompaniment.history.dialog.in" })}: {createdData}
              </Typography>
            </Box>

            <Typography variant="caption" color="text.secondary">
              {intl.formatMessage({ id: "accompaniment.history.dialog.changed.field" })}: Descrição
            </Typography>
          </Box>

          <Box sx={{ pb: 1 }}>
            <Box sx={{ pb: 1 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{
                  px: 1.2,
                  py: 0.4,
                  borderRadius: 1,
                  position: 'relative',
                  color: theme.palette.getContrastText(theme.palette.primary.main),
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  minWidth: 44,
                  textAlign: 'center',
                  boxShadow: '0 1px 0 rgba(0,0,0,0.04)',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    inset: 0,
                    borderRadius: 'inherit',
                    backgroundColor: 'var(--mexx-green-primary)',
                    filter: 'brightness(0.85)',
                    zIndex: 0,
                  }
                }}>
                  <Typography component="span" variant="caption" sx={{ fontWeight: 700, position: 'relative', zIndex: 1 }}>
                    {intl.formatMessage({ id: "accompaniment.history.dialog.from" })}
                  </Typography>
                </Box>

                <Paper variant="outlined" sx={{ p: 1, flex: 1, bgcolor: (theme.palette.mode === 'light' ? '#fbfdff' : theme.palette.background.paper) }}>
                  {renderDescription(isPrevExpanded, previusDescription, index, prevKey)}
                </Paper>
              </Box>
            </Box>

            <Box sx={{ pb: 0 }}>
              <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                <Box sx={{
                  px: 1.2,
                  py: 0.4,
                  borderRadius: 1,
                  bgcolor: 'var(--mexx-green-primary)',
                  color: theme.palette.getContrastText(theme.palette.primary.main),
                  fontWeight: 700,
                  fontSize: '0.75rem',
                  minWidth: 44,
                  textAlign: 'center',
                  boxShadow: '0 1px 0 rgba(0,0,0,0.04)'
                }}>
                  <Typography component="span" variant="caption" sx={{ fontWeight: 700 }}>
                    {intl.formatMessage({ id: "accompaniment.history.dialog.to" })}
                  </Typography>
                </Box>

                <Paper variant="outlined" sx={{ p: 1, flex: 1, bgcolor: (theme.palette.mode === 'light' ? '#fbfdff' : theme.palette.background.paper) }}>
                  {renderDescription(isNextExpanded, nextDescription, index, nextKey)}
                </Paper>
              </Box>
            </Box>
          </Box>
        </Paper>
      </Grid>
    )
  }

  React.useEffect(() => {
    let request = false;

    setState({ ...defaultState, loading: true });

    async function fetchHistory() {
      if (!open || !accompanimentSelected?.id || request) return;
      try {
        await handleGetAccompanimentHistoryById();
        setState(prevState => ({ ...prevState, loading: false }));
      } catch (err) {
        PapiroConsole.log(err);
        setState(prevState => ({ ...prevState, loading: false }));
        setMessage(
          true,
          'error',
          err.response && err.response.data && err.response.data.errors
            ? err.response.data.errors[0]
            : intl.formatMessage({ id: "process.error" })
        );
      }
    }

    fetchHistory();

    return () => { request = true; };
  }, [open, accompanimentSelected]);

  if (!open || !accompanimentSelected?.id) {
    return null;
  }

  return (
    <Dialog
      open={open}
      onClose={() => handleAccompanimentHistoryDialogOpen(null)}
      aria-labelledby="newuser-dialog-title"
      scroll="paper"
      maxWidth="md"
      fullWidth
    >
      <DialogTitle id="newuser-dialog-title" sx={{ pb: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6"><FormattedMessage id="accompaniment.history.dialog" /></Typography>
          <IconButton aria-label="close" size="small" onClick={() => handleAccompanimentHistoryDialogOpen(null)}>
            <ClearIcon fontSize="small" />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent
        dividers
        sx={{
          width: '100%',
          minHeight: '200px',
          p: 0
        }}
      >
        <Box sx={{ overflowY: 'auto', maxHeight: { xs: '50vh', sm: '55vh' }, p: 2 }}>
          <Grid container spacing={2}>

            {state.loading &&
              <Grid item xs={12} sx={{ display: "flex", alignItems: "center", justifyContent: "center", height: '12.5rem' }}>
                <CircularProgress color="primary" />
              </Grid>
            }

            {!state.loading && (!state.accompanimentList || state.accompanimentList.length <= 0) &&
              <Grid item xs={12} sx={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Typography variant="body2" color="text.secondary"><FormattedMessage id="rows.show" /></Typography>
              </Grid>
            }

            {!state.loading && state.accompanimentList && state.accompanimentList.length > 0 &&
              state.accompanimentList.map(renderHistory)}
          </Grid>
        </Box>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Box sx={{ width: '100%', display: 'flex', justifyContent: 'flex-end' }}>
          <Button
            variant='contained'
            color='primary'
            type="button"
            onClick={() => handleAccompanimentHistoryDialogOpen(null)}
            sx={{ minWidth: 120 }}
          >
            {intl.formatMessage({ id: "close" })}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}
